import { useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { MarkdownEditor } from '@/components/ui/MarkdownEditor/MarkdownEditor';
import { useToast } from '@/components/ui/Toast/ToastProvider';
import { useSession } from '@/services/auth';
import { ApiRequestError } from '@/services/api';
import { EDITABLE_DOCUMENTS, useCabinDocument, useUpdateCabinDocument } from '@/services/documents';
import { nb } from '@/i18n/nb';

/*
 * Full-screen editor at /admin/innhold/:slug. Owns the document fetch, the save mutation,
 * and the optimistic-concurrency state; the <MarkdownEditor> primitive is presentational.
 * Reached from the public Om hytta overlay (back → the page) or the Innhold list (back →
 * the list); the navigation state.from distinguishes them.
 */
export function AdminContentEditor() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { user, isLoading: sessionLoading } = useSession();
  const { data: doc, isLoading, refetch } = useCabinDocument(slug);
  const update = useUpdateCabinDocument(slug);
  const [conflict, setConflict] = useState(false);

  const meta = EDITABLE_DOCUMENTS.find((d) => d.slug === slug);
  const fromPublic = (location.state as { from?: string } | null)?.from === 'public';
  const returnLabel = fromPublic ? nb.documents.editor.backToPage : nb.documents.editor.backToList;

  // Hooks run first; only then the guards. Editing is admin-only (server enforces it too).
  if (sessionLoading || isLoading) return null;
  if (user?.role !== 'admin') return <Navigate to="/" replace />;
  if (!meta || !doc) return <Navigate to="/admin/innhold" replace />;

  // Capture the now-narrowed values so the handler closures keep their non-null types.
  const { title, previewVariant } = meta;
  const { content, version, updatedByName } = doc;

  function close() {
    navigate(fromPublic ? '/' : '/admin/innhold');
  }

  function save(nextContent: string) {
    setConflict(false);
    update.mutate(
      { content: nextContent, version },
      {
        onSuccess: (saved) =>
          toast({
            title: nb.documents.editor.savedToastTitle,
            body: nb.documents.editor.savedToastBody
              .replace('{tittel}', title)
              .replace('{versjon}', String(saved.data.version)),
          }),
        onError: (error) => {
          // A stale version is the expected concurrent-edit case → show the banner.
          if (error instanceof ApiRequestError && error.status === 409) {
            setConflict(true);
          } else {
            toast({
              title: nb.documents.editor.saveErrorTitle,
              body: nb.documents.editor.saveErrorBody,
              error: true,
            });
          }
        },
      },
    );
  }

  async function reload() {
    const result = await refetch();
    setConflict(false);
    toast({
      title: nb.documents.editor.reloadedToastTitle,
      body: nb.documents.editor.reloadedToastBody.replace(
        '{versjon}',
        String(result.data?.version ?? ''),
      ),
    });
  }

  return (
    <MarkdownEditor
      title={title}
      slug={slug}
      content={content}
      version={version}
      updatedByName={updatedByName}
      conflict={conflict}
      saving={update.isPending}
      saved={update.isSuccess}
      previewVariant={previewVariant}
      returnLabel={returnLabel}
      onSave={save}
      onReload={reload}
      onClose={close}
    />
  );
}
